import { Component, type ErrorInfo, type ReactNode } from "react";
import { Box, Button, Stack, Typography } from "../ds";
import { RefreshCw } from "lucide-react";
import { RavenIcon } from "@ravenappdev/raven-ui";

interface Props {
  children: ReactNode;
  /**
   * Optional render override for the fallback UI. Useful for scoped
   * boundaries that want a more contextual message than the generic
   * "Something went wrong" card.
   */
  fallback?: (error: Error, reset: () => void) => ReactNode;
  /**
   * Optional reporting hook fired after the render error is logged.
   * Forward to Sentry, Datadog, or any other error reporter — failures
   * inside the hook are swallowed so they can't break the fallback UI.
   */
  onError?: (error: Error, info: ErrorInfo) => void;
}

interface State {
  error: Error | null;
}

/**
 * App-level error boundary. Catches render-phase errors thrown from any
 * descendant component and shows a recovery card instead of a blank
 * screen. Errors are logged to the dev console; pass an `onError` prop
 * to forward them to an error reporter.
 *
 * Note: this only catches *render* errors. Async errors (axios failures,
 * promise rejections) still need to be handled by react-query / try-catch
 * at the call site — they don't propagate up to a boundary.
 */
export default class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // eslint-disable-next-line no-console
    console.error("ErrorBoundary caught:", error, info);
    try {
      this.props.onError?.(error, info);
    } catch (hookError) {
      // eslint-disable-next-line no-console
      console.error("ErrorBoundary onError hook threw:", hookError);
    }
  }

  reset = (): void => {
    this.setState({ error: null });
  };

  render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;
    if (this.props.fallback) return this.props.fallback(error, this.reset);
    return (
      <Box
        sx={{
          minHeight: "60vh",
          display: "grid",
          placeItems: "center",
          px: 3,
          py: 6,
        }}
      >
        <Box
          sx={{
            maxWidth: 480,
            width: "100%",
            p: 4,
            borderRadius: "16px",
            border: "1px solid",
            borderColor: "divider",
            bgcolor: "background.paper",
            textAlign: "center",
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
            Something went wrong
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            We hit an unexpected error rendering this page. Try again, or
            reload the app if the issue persists.
          </Typography>
          {process.env.NODE_ENV !== "production" && (
            <Box
              sx={{
                textAlign: "left",
                bgcolor: "action.hover",
                color: "text.primary",
                fontFamily: "monospace",
                fontSize: 12,
                p: 1.5,
                borderRadius: 1,
                mb: 3,
                whiteSpace: "pre-wrap",
                overflowWrap: "anywhere",
                maxHeight: 200,
                overflow: "auto",
              }}
            >
              {error.message}
            </Box>
          )}
          <Stack
            direction="row"
            spacing={1.5}
            justifyContent="center"
          >
            <Button variant="outlined" onClick={this.reset}>
              Try again
            </Button>
            <Button
              variant="contained"
              startIcon={<RavenIcon icon={RefreshCw} />}
              onClick={() => window.location.reload()}
            >
              Reload app
            </Button>
          </Stack>
        </Box>
      </Box>
    );
  }
}
